// src/components/Navbar.jsx
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
	getPartnerOptions,
	getPartnerLabel,
	getDefaultPartnerAlias
} from "../utils/partnerMap";

const MENU_ITEMS = [
	{ label: "Dashboard", to: "/dashboard", icon: "▦", exact: true },
	{ label: "Add New Partner", to: "/AddNewPartner", icon: "👤" },
	{ label: "Add New Payment", to: "/AddNewPayment", icon: "🏦" },
	{ label: "Payment Details", to: "/PaymentDetails", icon: "🏛️", exact: true }
];

const USER_ALLOWED = new Set(["/dashboard", "/PaymentDetails"]);

const Navbar = () => {
	const {
		logout,
		role,
		partnerName, // display only (from JWT)
		activePartnerAlias, // admin-selected alias (sessionStorage via AuthContext)
		setAdminPartnerAlias,
		loading
	} = useAuth();

	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const [isPartnerDropdownOpen, setIsPartnerDropdownOpen] = useState(false);

	const dropdownRef = useRef(null);

	const toggleSidebar = useCallback(() => {
		setIsSidebarOpen((prev) => !prev);
	}, []);
	const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

	const closePartnerDropdown = useCallback(() => {
		setIsPartnerDropdownOpen(false);
	}, []);
	const togglePartnerDropdown = useCallback(() => {
		setIsPartnerDropdownOpen((p) => !p);
	}, []);

	// Close partner dropdown on outside click + ESC
	useEffect(() => {
		if (!isPartnerDropdownOpen) return;

		const onKeyDown = (e) => {
			if (e.key === "Escape") closePartnerDropdown();
		};

		const onMouseDown = (e) => {
			if (!dropdownRef.current) return;
			if (!dropdownRef.current.contains(e.target)) closePartnerDropdown();
		};

		window.addEventListener("keydown", onKeyDown);
		window.addEventListener("mousedown", onMouseDown);
		return () => {
			window.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("mousedown", onMouseDown);
		};
	}, [isPartnerDropdownOpen, closePartnerDropdown]);

	const filteredMenuItems = useMemo(() => {
		if (role === "fl-admin") return MENU_ITEMS;
		if (role === "user")
			return MENU_ITEMS.filter((m) => USER_ALLOWED.has(m.to));
		return MENU_ITEMS.filter((m) => m.to === "/dashboard");
	}, [role]);

	const partnerOptions = useMemo(() => getPartnerOptions() || [], []);
	const defaultAlias = useMemo(() => getDefaultPartnerAlias(), []);

	// Effective alias used for admin
	const effectiveAlias = useMemo(() => {
		if (role !== "fl-admin") return null;
		return activePartnerAlias || defaultAlias;
	}, [role, activePartnerAlias, defaultAlias]);

	const currentAliasLabel = useMemo(() => {
		if (role !== "fl-admin") return "";
		return getPartnerLabel(effectiveAlias) || "Select Partner";
	}, [role, effectiveAlias]);

	const handleAliasSelect = useCallback(
		(aliasKey) => {
			const v = String(aliasKey || "").trim();
			if (!v) return;

			if (v === activePartnerAlias) {
				closePartnerDropdown();
				return;
			}

			setAdminPartnerAlias?.(v);
			closePartnerDropdown();
		},
		[activePartnerAlias, setAdminPartnerAlias, closePartnerDropdown]
	);

	// Optional: avoid showing navbar content while auth is resolving
	if (loading) return null;

	return (
		<>
			{isSidebarOpen && (
				<div
					className="fixed inset-0 z-30 bg-black/40 lg:hidden"
					onClick={() => {
						closeSidebar();
						closePartnerDropdown();
					}}
				/>
			)}

			<aside
				className={`fixed inset-y-0 left-0 z-40 flex w-56 flex-col bg-[#06223b] px-4 py-5 text-slate-50 transform transition-transform duration-200 ${
					isSidebarOpen ? "translate-x-0" : "-translate-x-full"
				} lg:translate-x-0`}
			>
				<div className="mb-4 flex w-full justify-center">
					<img
						src="/fconnect33_Grey.png"
						alt="Logo"
						className="w-32 object-contain"
						draggable="false"
					/>
				</div>

				{/* Partner Alias switch ONLY for admin */}
				{role === "fl-admin" && (
					<div
						className="relative mb-4"
						ref={dropdownRef}
					>
						<button
							type="button"
							onClick={togglePartnerDropdown}
							className="flex w-full items-center gap-2 rounded-lg border border-emerald-400 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-emerald-300 hover:bg-emerald-400/10"
							aria-haspopup="listbox"
							aria-expanded={isPartnerDropdownOpen}
						>
							<span className="flex h-5 w-5 items-center justify-center rounded-full bg-sky-500 text-[10px] text-white">
								🌐
							</span>

							<span className="leading-tight text-left flex-1">
								<span className="block">Partner Alias</span>
								<span className="block normal-case text-[11px] text-slate-200 truncate">
									{currentAliasLabel}
								</span>
							</span>

							<span className="text-slate-200">
								{isPartnerDropdownOpen ? "▴" : "▾"}
							</span>
						</button>

						{isPartnerDropdownOpen && (
							<div className="absolute left-0 top-full mt-2 w-full rounded-lg border border-slate-700 bg-[#081f33] shadow-xl z-50 overflow-hidden">
								<div
									className="max-h-64 overflow-auto"
									role="listbox"
								>
									{partnerOptions.map((p) => {
										const active = effectiveAlias === p;

										return (
											<button
												key={p}
												type="button"
												onClick={() => handleAliasSelect(p)}
												className={[
													"w-full px-3 py-2 text-left text-[12px] transition",
													active
														? "bg-emerald-400/15 text-emerald-200"
														: "text-slate-100 hover:bg-white/10"
												].join(" ")}
												role="option"
												aria-selected={active}
											>
												<div className="font-semibold">
													{getPartnerLabel(p)}
												</div>
												<div className="text-[10px] text-slate-300">{p}</div>
											</button>
										);
									})}
								</div>
							</div>
						)}
					</div>
				)}

				<div className="mb-3 w-full border-b border-slate-600/40" />

				<nav className="flex flex-1 flex-col gap-1 text-[12px] font-semibold">
					{filteredMenuItems.map((item) => (
						<NavLink
							key={item.to}
							to={item.to}
							end={item.exact}
							className={({ isActive }) =>
								[
									"flex items-center gap-3 rounded-l-md px-3 py-2 uppercase tracking-wide transition-all",
									isActive
										? "bg-white text-slate-900 shadow-[0_10px_22px_rgba(0,0,0,0.45)]"
										: "text-slate-100 hover:bg-white/10 hover:text-white"
								].join(" ")
							}
							onClick={() => {
								closeSidebar();
								closePartnerDropdown();
							}}
						>
							<span className="text-base">{item.icon}</span>
							<span>{item.label}</span>
						</NavLink>
					))}
				</nav>

				<button
					type="button"
					onClick={logout}
					className="mt-4 rounded-md bg-red-500 px-3 py-1.5 text-xs font-medium hover:bg-red-600"
				>
					Logout
				</button>
			</aside>

			<header className="fixed top-0 right-0 left-0 lg:left-56 z-20 flex h-14 items-center justify-between border-b border-slate-300 bg-white px-3 sm:px-4 lg:px-6">
				<div className="flex items-center gap-3">
					<button
						type="button"
						onClick={toggleSidebar}
						className="mr-1 inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 lg:hidden"
						aria-label="Toggle sidebar"
					>
						☰
					</button>

					<div className="flex flex-col leading-[100%]">
						<div className="flex items-baseline gap-1">
							<span className="text-[11px] uppercase tracking-wide text-slate-500">
								Partner :
							</span>
							<span className="text-sm font-semibold text-slate-800">
								{String(partnerName || "—").toUpperCase()}
							</span>
						</div>

						{role === "fl-admin" && (
							<div className="text-[10px] text-slate-500 mt-1">
								Data Partner:{" "}
								<span className="font-semibold text-slate-700">
									{String(effectiveAlias || "—")}
								</span>
							</div>
						)}
					</div>
				</div>

				<div className="flex items-center gap-3">
					{/* ✅ ONLY UPDATED: Help button hover card */}
					<div className="relative group">
						<button
							type="button"
							className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 bg-white text-xs text-slate-700 hover:bg-slate-100"
							aria-label="Help"
						>
							?
						</button>

						{/* shows while cursor is on button OR card */}
						<div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-150 absolute right-0 top-full mt-2 w-[320px] rounded-xl border border-slate-200 bg-white shadow-xl z-50">
							<div className="p-4">
								<div className="text-center">
									<div className="text-[13px] font-semibold text-slate-800">
										We would love to hear you more
									</div>
									<div className="mt-0.5 text-[11px] font-medium text-slate-500">
										For any query, contact
									</div>
								</div>

								<div className="mt-3 flex items-center gap-3">
									{/* Avatar (replace with real image if you want) */}
									<div className="h-14 w-14 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 font-bold">
										AJ
									</div>

									<div className="min-w-0">
										<div className="text-[14px] font-semibold text-slate-900">
											Asif Uddin Joy
										</div>
										<div className="text-[12px] font-medium text-slate-500">
											Project Manager
										</div>
									</div>
								</div>

								<div className="mt-4 space-y-2 text-[14px]">
									<div className="flex items-start gap-2">
										<span className="mt-[1px]">✉️</span>
										<div className="leading-tight">
											<div className="font-semibold text-slate-700">Email:</div>
											<div className="text-emerald-600 font-semibold">
												asif@fconnect.io
											</div>
										</div>
									</div>

									<div className="flex items-start gap-2">
										<span className="mt-[1px]">💬</span>
										<div className="leading-tight">
											<div className="font-semibold text-slate-700">Skype:</div>
											<div className="text-emerald-600 font-semibold">
												Asif Uddin Joy
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					{/* ✅ END UPDATE */}
				</div>
			</header>
		</>
	);
};

export default Navbar;
